import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowLeft, Send, Inbox, Trash2, MailCheck, Mail, MessageSquarePlus, Loader2 } from "lucide-react";
import Header from "../components/Header";
import BottomNav from "../components/BottomNav";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(iso, dateLocale) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(dateLocale, {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function UserForm() {
  const { t } = useTranslation();
  const CATEGORIES = t("suggestions.categories", { returnObjects: true });
  const [content, setContent] = useState("");
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!content.trim()) {
      toast.error(t("suggestions.emptyMessageError"));
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/suggestions`, { content: content.trim(), category }, { withCredentials: true });
      setContent("");
      setSent(true);
      toast.success(t("suggestions.sendSuccess"));
    } catch (e) {
      toast.error(t("suggestions.sendError"));
    } finally {
      setSending(false);
    }
  };

  return (
    <section className="vs-card p-5" data-testid="suggestion-form">
      <div className="flex items-start gap-3 mb-3">
        <div className="shrink-0 w-12 h-12 rounded-xl flex items-center justify-center"
          style={{ background: "var(--gradient-vine)" }}>
          <MessageSquarePlus size={22} color="white" strokeWidth={2.4} />
        </div>
        <div>
          <h2 className="heading text-xl font-extrabold">{t("suggestions.formTitle")}</h2>
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>
            {t("suggestions.formSubtitle")}
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-3">
        <div>
          <label className="text-xs font-semibold uppercase tracking-wider"
            style={{ color: "var(--text-muted)" }}>
            {t("suggestions.typeLabel")}
          </label>
          <div className="flex flex-wrap gap-2 mt-1">
            {CATEGORIES.map((c) => (
              <button key={c} type="button" onClick={() => setCategory(c)}
                className="text-xs px-3 py-1.5 rounded-full transition-colors"
                style={{
                  background: category === c ? "var(--primary)" : "var(--secondary)",
                  color: category === c ? "white" : "var(--text-primary)",
                  border: "1.5px solid var(--border)",
                  fontWeight: category === c ? 700 : 500,
                }}
                data-testid={`category-${c.toLowerCase()}`}>
                {c}
              </button>
            ))}
          </div>
        </div>

        <textarea
          className="vs-input w-full"
          rows={6}
          maxLength={4000}
          placeholder={t("suggestions.textareaPlaceholder")}
          value={content}
          onChange={(e) => { setContent(e.target.value); setSent(false); }}
          data-testid="suggestion-textarea"
        />
        <div className="flex items-center justify-between">
          <span className="text-xs" style={{ color: "var(--text-muted)" }}>
            {content.length}/4000
          </span>
          <button type="submit" disabled={sending || !content.trim()}
            className="vs-btn-primary inline-flex items-center gap-2"
            data-testid="suggestion-submit">
            {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
            {sending ? t("suggestions.sendingButton") : t("suggestions.sendButton")}
          </button>
        </div>

        {sent && (
          <div className="text-sm p-3 rounded-xl"
            style={{ background: "var(--secondary)", border: "1.5px solid var(--border)", color: "var(--text-primary)" }}
            data-testid="suggestion-sent-confirm">
            {t("suggestions.sentConfirm")}
          </div>
        )}
      </form>
    </section>
  );
}

function AdminInbox() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === "fr" ? "fr-FR" : "it-IT";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all / unread

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/suggestions`, { withCredentials: true });
      setItems(r.data?.items || []);
    } catch {
      toast.error(t("suggestions.loadError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const markRead = async (id) => {
    try {
      await axios.patch(`${API}/suggestions/${id}/read`, {}, { withCredentials: true });
      setItems((prev) => prev.map((x) => x.id === id ? { ...x, read: true } : x));
    } catch { toast.error(t("suggestions.markReadError")); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("suggestions.deleteConfirm"))) return;
    try {
      await axios.delete(`${API}/suggestions/${id}`, { withCredentials: true });
      setItems((prev) => prev.filter((x) => x.id !== id));
      toast.success(t("suggestions.deleteSuccess"));
    } catch { toast.error(t("suggestions.deleteError")); }
  };

  const filtered = filter === "unread" ? items.filter((x) => !x.read) : items;
  const unread = items.filter((x) => !x.read).length;

  return (
    <section className="space-y-4" data-testid="admin-inbox">
      <div className="vs-card p-4 flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center"
          style={{ background: "var(--gradient-vine)" }}>
          <Inbox size={22} color="white" strokeWidth={2.4} />
        </div>
        <div className="flex-1">
          <div className="heading text-lg font-extrabold">{t("suggestions.inboxTitle")}</div>
          <div className="text-xs" style={{ color: "var(--text-muted)" }}>
            {t("suggestions.inboxSummary", { count: items.length, unread })}
          </div>
        </div>
        <div className="flex gap-1.5">
          <button onClick={() => setFilter("all")}
            className="text-xs px-3 py-1.5 rounded-full"
            style={{
              background: filter === "all" ? "var(--primary)" : "var(--secondary)",
              color: filter === "all" ? "white" : "var(--text-primary)",
              border: "1.5px solid var(--border)", fontWeight: 700,
            }}
            data-testid="filter-all">{t("suggestions.filterAll")}</button>
          <button onClick={() => setFilter("unread")}
            className="text-xs px-3 py-1.5 rounded-full"
            style={{
              background: filter === "unread" ? "var(--primary)" : "var(--secondary)",
              color: filter === "unread" ? "white" : "var(--text-primary)",
              border: "1.5px solid var(--border)", fontWeight: 700,
            }}
            data-testid="filter-unread">{t("suggestions.filterUnread", { count: unread })}</button>
        </div>
      </div>

      {loading && (
        <div className="vs-card p-6 text-center">
          <Loader2 size={24} className="animate-spin mx-auto" style={{ color: "var(--primary)" }} />
          <div className="text-sm mt-2" style={{ color: "var(--text-muted)" }}>{t("suggestions.loading")}</div>
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="vs-card p-8 text-center" data-testid="empty-inbox">
          <Inbox size={36} className="mx-auto mb-2" style={{ color: "var(--text-muted)" }} />
          <div className="heading font-bold">{t("suggestions.emptyTitle")}</div>
          <div className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>
            {filter === "unread" ? t("suggestions.emptyUnread") : t("suggestions.emptyAll")}
          </div>
        </div>
      )}

      {!loading && filtered.map((s) => (
        <article key={s.id} className="vs-card p-4"
          style={{ borderLeft: !s.read ? "4px solid var(--gold)" : undefined }}
          data-testid={`suggestion-${s.id}`}>
          <div className="flex items-start gap-3 mb-2">
            {s.user_picture ? (
              <img src={s.user_picture} alt="" className="w-10 h-10 rounded-full object-cover"
                referrerPolicy="no-referrer"
                style={{ border: "2px solid var(--border)" }} />
            ) : (
              <div className="w-10 h-10 rounded-full flex items-center justify-center font-extrabold"
                style={{ background: "var(--primary)", color: "white" }}>
                {(s.user_name || "?").slice(0, 1).toUpperCase()}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-extrabold heading text-sm truncate">{s.user_name || t("suggestions.anonymous")}</span>
                <span className="text-xs px-2 py-0.5 rounded-full"
                  style={{ background: "var(--secondary)", border: "1px solid var(--border)" }}>
                  {s.category || t("suggestions.categories", { returnObjects: true })[0]}
                </span>
                {!s.read && (
                  <span className="text-xs px-2 py-0.5 rounded-full font-bold"
                    style={{ background: "var(--gold)", color: "#5a3500" }}>
                    {t("suggestions.newBadge")}
                  </span>
                )}
              </div>
              <div className="text-xs truncate" style={{ color: "var(--text-muted)" }}>
                {s.user_email} · {formatDate(s.created_at, dateLocale)}
              </div>
            </div>
          </div>
          <p className="text-sm whitespace-pre-wrap mb-3" style={{ color: "var(--text-primary)" }}>
            {s.content}
          </p>
          <div className="flex flex-wrap gap-2">
            <a href={`mailto:${s.user_email}?subject=${encodeURIComponent(t("suggestions.replySubject"))}`}
              className="vs-btn-secondary text-xs inline-flex items-center gap-1.5"
              data-testid={`reply-${s.id}`}>
              <Mail size={14} /> {t("suggestions.replyButton")}
            </a>
            {!s.read && (
              <button onClick={() => markRead(s.id)}
                className="vs-btn-secondary text-xs inline-flex items-center gap-1.5"
                data-testid={`mark-read-${s.id}`}>
                <MailCheck size={14} /> {t("suggestions.markReadButton")}
              </button>
            )}
            <button onClick={() => remove(s.id)}
              className="vs-btn-secondary text-xs inline-flex items-center gap-1.5"
              style={{ color: "var(--terracotta)" }}
              data-testid={`delete-${s.id}`}>
              <Trash2 size={14} /> {t("suggestions.deleteButton")}
            </button>
          </div>
        </article>
      ))}
    </section>
  );
}

export default function Suggestions() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;

  return (
    <div className="min-h-screen pb-24" data-testid="suggestions-page">
      <Header
        title={isAdmin ? t("suggestions.headerTitleAdmin") : t("suggestions.headerTitleUser")}
        subtitle={t("suggestions.headerSubtitle")}
        right={
          <button onClick={() => navigate(-1)} className="p-2 rounded-lg"
            style={{ border: "2px solid var(--border)" }}
            aria-label={t("suggestions.backAriaLabel")} data-testid="suggestions-back">
            <ArrowLeft size={18} />
          </button>
        }
      />
      <main className="max-w-2xl mx-auto px-4 py-5 space-y-5">
        {isAdmin ? <AdminInbox /> : <UserForm />}
      </main>
      <BottomNav />
    </div>
  );
}
